import { useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import WorkCard from "@/components/base/WorkCard";
import useSeo from "@/hooks/useSeo";
import { works } from "@/mocks/works";

const typeLabel: Record<string, string> = {
  new: "新番",
  movie: "剧场版",
  ova: "OVA",
  classic: "经典",
};

export default function WorkDetailPage() {
  const { id = "" } = useParams();
  const work = works.find((w) => w.id === id);

  const related = useMemo(() => {
    if (!work) return [];
    return works
      .filter((w) => w.id !== work.id && (w.category === work.category || w.type === work.type))
      .sort((a, b) => b.score - a.score)
      .slice(0, 4);
  }, [work]);

  useSeo({
    title: work
      ? `${work.title} - ${work.category}番剧介绍 | 漫番志`
      : "作品详情 | 漫番志",
    description: work
      ? `${work.title}是一部${work.category}${typeLabel[work.type]}，评分${work.score.toFixed(1)}，由${work.studio}制作。${work.description}`
      : "漫番志作品详情页",
    keywords: `${work?.title},${work?.category}番,${work?.tags.join(",")||""},动漫`,
    jsonLd: work
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://freshanime.github.io/" },
              { "@type": "ListItem", position: 2, name: `${work.category}番`, item: "https://freshanime.github.io/ranking" },
              { "@type": "ListItem", position: 3, name: work.title },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "TVSeries",
            name: work.title,
            description: work.description,
            genre: work.category,
            contentRating: `${work.score.toFixed(1)}`,
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: work.score,
              ratingCount: 1280,
            },
          },
        ]
      : undefined,
  });

  if (!work) return <Navigate to="/" replace />;

  return (
    <Layout>
      <div className="relative overflow-hidden border-b border-background-300/40 bg-background-100 pt-28 md:pt-32">
        <div className="absolute inset-0 opacity-30">
          <img
            src={work.image}
            alt=""
            className="h-full w-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-background-50/80 via-background-50/90 to-background-50" />
        <div className="relative mx-auto max-w-7xl px-4 pb-10 md:px-6 md:pb-14">
          <nav aria-label="面包屑" className="mb-6 flex flex-wrap items-center gap-2 text-sm text-foreground-500">
            <Link to="/" className="flex items-center gap-1 hover:text-primary-400">
              <i className="ri-home-4-line" /> 首页
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/ranking" className="hover:text-primary-400">{work.category}番</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-700">{work.title}</span>
          </nav>

          <div className="flex flex-col gap-8 md:flex-row">
            <div className="shrink-0">
              <div className="w-48 overflow-hidden rounded-2xl ring-1 ring-background-300/60 md:w-60">
                <img
                  src={work.image}
                  alt={`${work.title} 动画海报`}
                  title={`${work.title} - 高清海报`}
                  className="aspect-[4/5] w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-full bg-primary-500 px-3 py-1 text-xs font-bold text-background-50">
                  {typeLabel[work.type]}
                </span>
                <span className="rounded-full bg-accent-500/20 px-3 py-1 text-xs font-bold text-accent-400">
                  {work.category}番
                </span>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-bold ${
                    work.status === "连载中"
                      ? "bg-secondary-500/20 text-secondary-400"
                      : "bg-background-200 text-foreground-600"
                  }`}
                >
                  {work.status}
                </span>
              </div>
              <h1 className="mt-4 font-heading text-3xl text-foreground-950 md:text-4xl">{work.title}</h1>
              <p className="mt-3 flex items-center gap-1 text-lg font-bold text-secondary-400">
                <i className="ri-star-fill" /> {work.score.toFixed(1)}
                <span className="text-xs font-normal text-foreground-500">站内评分</span>
              </p>

              <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3">
                {[
                  { label: "类型", value: `${work.category}番 · ${typeLabel[work.type]}` },
                  { label: "上映年份", value: `${work.year}` },
                  { label: "集数 / 片长", value: work.episodes },
                  { label: "制作公司", value: work.studio },
                  { label: "导演", value: work.director },
                  { label: "播放状态", value: work.status },
                ].map((item) => (
                  <div key={item.label}>
                    <p className="text-xs text-foreground-500">{item.label}</p>
                    <p className="mt-1 text-sm font-bold text-foreground-950">{item.value}</p>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                {work.tags.map((tag) => (
                  <span
                    key={tag}
                    className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-700 ring-1 ring-background-300/50"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <section className="rounded-2xl bg-background-100 p-6 ring-1 ring-background-300/60 md:p-8">
          <h2 className="flex items-center gap-2 font-heading text-xl text-foreground-950">
            <i className="ri-file-text-line text-primary-400" /> 剧情简介
          </h2>
          <p className="mt-4 leading-loose text-foreground-700">{work.description}</p>
          <p className="mt-4 leading-loose text-foreground-600">
            作为一部{work.category}题材的{typeLabel[work.type]}，{work.title}
            在叙事节奏与情感表达上都相当出彩，无论是画面、配乐还是人物塑造都值得细细品味。
            如果你正在寻找一部值得花时间慢慢看的作品，不妨把它加入你的追番片单。
          </p>
        </section>

        <section className="mt-14">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="flex items-center gap-2 font-heading text-xl text-foreground-950">
              <i className="ri-thumb-up-line text-accent-400" /> 相似推荐
            </h2>
            <Link to="/ranking" className="flex items-center gap-1 text-sm font-bold text-primary-400 hover:text-primary-300">
              更多 <i className="ri-arrow-right-line" />
            </Link>
          </div>
          {related.length > 0 ? (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
              {related.map((w) => (
                <WorkCard key={w.id} work={w} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-foreground-600">暂未找到相似作品，去排行榜逛逛吧。</p>
          )}
        </section>
      </div>
    </Layout>
  );
}